// Create a new preset. The editor renders the catalog-driven form;
// on submit we POST to /admin/mm/presets and navigate to the freshly
// created preset's detail page.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateAffected } from "@/lib/routeRefresh";
import { PresetEditor } from "@/components/PresetEditor";
import { createPreset } from "@/lib/queries";
import { APIError } from "@/lib/api";
import { emptyRunConfig } from "@/lib/types";
import { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";

export const Route = createFileRoute("/_app/presets/new")({
  component: NewPresetPage,
});

function NewPresetPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: createPreset,
    onSuccess: (p) => {
      invalidateAffected(qc, "presets");
      navigate({ to: "/presets/$id", params: { id: p.preset_id } });
    },
    onError: (e) => setErr(e instanceof APIError ? e.message : t("preset.error.create")),
  });

  return (
    <div className="space-y-6">
      <header>
        <div className="text-2xs uppercase tracking-widest t-mute">
          <Link to="/presets" className="hover:t-emph">{t("presets.title")}</Link>
          {" / "}
          <span className="text-t-1">{t("preset.newBreadcrumb")}</span>
        </div>
        <h1 className="page-title">{t("preset.newTitle")}</h1>
        <p className="page-subtitle">
          {t("preset.newSubtitle")}
        </p>
      </header>
      <PresetEditor
        initial={{ name: "", description: "", config: emptyRunConfig() }}
        submitLabel={t("preset.create")}
        submitting={mut.isPending}
        errorMessage={err}
        onSubmit={(v) => {
          setErr(null);
          mut.mutate({ name: v.name, description: v.description, config: v.config });
        }}
      />
    </div>
  );
}
